import { ConnectionStatus } from 'botframework-directlinejs';
import ReactWebChat, { StyleOptions, createDirectLine, createStore } from 'botframework-webchat';
import { AdaptiveCardsStyleOptions } from 'botframework-webchat/lib/adaptiveCards/AdaptiveCardsStyleOptions';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { FiX } from 'react-icons/fi';

import { generateCopilotToken } from 'api/copilot';
import { authClient } from 'auth';
import { IconButton } from 'components/button';
import { OrganizationTheme } from 'interfaces';
import { CopilotConfiguration, CopilotToken } from 'interfaces/copilot';
import { styled } from 'stitches';

const COPILOT_VERSION = '5';
const COPILOT_BANNER_HEIGHT = '50px';

const CopilotBanner = styled('div', {
  alignItems: 'center',
  backgroundColor: '$navbar-bg',
  display: 'flex',
  height: COPILOT_BANNER_HEIGHT,
});

const CopilotHeader = styled('h1', {
  color: '$navbar-text',
  fontSize: '$xl',
  lineHeight: '20px',
  margin: '0',
  padding: '0 20px',
});

const CopilotDismiss = styled(IconButton, {
  color: '$navbar-text',
  position: 'absolute',
  right: '5px',
  top: '5px',
});

const CopilotChatContainer = styled('div', {
  width: '100%',
  height: `calc(100% - ${COPILOT_BANNER_HEIGHT})`,
});

type CopilotLayoutProps = {
  copilotConfiguration: CopilotConfiguration;
  organizationTheme: OrganizationTheme | null;
  onDismiss?: () => void;
};

export default function CopilotLayout(props: CopilotLayoutProps) {
  const {
    copilotConfiguration: { directLineEndpoint },
    organizationTheme,
    onDismiss,
  } = props;

  const [copilotAccessToken, setCopilotAccessToken] = useState<CopilotToken | null>(null);

  const styleOptions: StyleOptions & AdaptiveCardsStyleOptions = {
    hideUploadButton: true,
    suggestedActionLayout: 'stacked',

    accent: organizationTheme?.primaryColor,
    botAvatarBackgroundColor: '#FFFFFF',
    botAvatarInitials: 'AI',
    botAvatarImage: `${process.env.REACT_APP_DECISIO_PATH}/logo?color=${encodeURIComponent(
      organizationTheme?.navbarColor || ''
    )}`,

    bubbleMaxWidth: 800,
  };

  useEffect(() => {
    const generateAccessToken = async () => {
      const token = await generateCopilotToken();
      setCopilotAccessToken(token);
    };

    generateAccessToken();
  }, []);

  const directLine = useMemo(() => {
    if (!copilotAccessToken) {
      return null;
    }

    return createDirectLine({
      domain: new URL('v3/directline', directLineEndpoint).toString(),
      token: copilotAccessToken?.token,
    });
  }, [copilotAccessToken, directLineEndpoint]);

  useEffect(() => {
    if (!directLine) {
      return;
    }

    const subscription = directLine.connectionStatus$.subscribe((status) => {
      if (status === ConnectionStatus.Online) {
        const copilotVersion =
          process.env.REACT_APP_COPILOT_PATH_SEGMENT &&
          window.location.pathname.startsWith(`/${process.env.REACT_APP_COPILOT_PATH_SEGMENT}`)
            ? new URLSearchParams(window.location.search).get('version') || COPILOT_VERSION
            : COPILOT_VERSION;

        directLine
          .postActivity({
            name: 'startConversation',
            type: 'event',
            from: { id: '' }, // Intentionally empty to signal that this is NOT a secure way of identifying the user. Use the token instead to query the user's identity.
            value: {
              DecisioUrl: window.location.origin,
              Version: copilotVersion,
            },
          })
          .subscribe();

        // Only send the event once, unsubscribe after the event is sent.
        subscription.unsubscribe();
      }
    });

    // Cleanup subscription on unmount
    return () => subscription.unsubscribe();
  }, [directLine]);

  const store = useMemo(
    () =>
      createStore({}, () => (next: any) => async (action: any) => {
        if (action.type === 'DIRECT_LINE/INCOMING_ACTIVITY') {
          const { activity } = action.payload;

          if (activity.attachments?.[0]?.contentType === 'application/vnd.microsoft.card.oauth') {
            // Extract the endpoint to which the token is posted
            const postEndpoint = activity.attachments?.[0].content.tokenPostResource.sasUrl;

            const accessToken = await authClient.getAccessToken();

            if (accessToken) {
              fetch(postEndpoint, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  token: accessToken,
                }),
              })
                .then(() => {
                  // Token sent successfully, do not show the login card
                })
                .catch((error) => {
                  console.error('An error occurred:', error);
                  // Token was not sent successfully, display the login card to the user
                  return next(action);
                });
              return;
            } else {
              next(action);
            }
          }
        }

        return next(action);
      }),
    []
  );

  const handleDismiss = useCallback(() => {
    onDismiss?.();
  }, [onDismiss]);

  return (
    <>
      <CopilotBanner>
        <CopilotHeader>Dek</CopilotHeader>
        <CopilotDismiss onClick={handleDismiss}>
          <FiX />
        </CopilotDismiss>
      </CopilotBanner>

      <CopilotChatContainer>
        {directLine && (
          <ReactWebChat
            directLine={directLine}
            locale={document.documentElement.lang || 'en'}
            styleOptions={styleOptions}
            store={store}
          />
        )}
      </CopilotChatContainer>
    </>
  );
}
